import { FC, useMemo } from 'react'

import { usePortal } from '../../../../../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { EmbroideryDisclosure } from '../../../../../../brunswick/components/EmbroideryDisclosure'
import { EmbroideryService } from '../../../../../../containers/EmbroideryService'
import { Output as AddToBagOutput } from '../../../../../../lib/hooks/useBag'
import { EmbroideryCtaSection } from '../../../../../embroidery/EmbroideryCtaSection'
import { EmbroiderySectionWrapper } from '../../../../../embroidery/EmbroiderySectionWrapper'
import { EmbroiderySummary } from '../../../../../embroidery/EmbroiderySummary'
import { PortalEmbroiderySection } from '../PortalEmbroiderySection'

interface Props
  extends Partial<Pick<AddToBagOutput, 'resetInvalidBagClickStatus' | 'addToBagErrors'>> {
  isPortalColorSelected?: boolean
  embroiderable: boolean
}

export const EmbroiderySection: FC<Props> = ({
  isPortalColorSelected,
  resetInvalidBagClickStatus,
  addToBagErrors,
  embroiderable,
}) => {
  const {
    selectedEmbroideryFormOptions,
    selectedEmbroideryOptions,
    confirmAddEmbroidery,
    categoryAnalyticsId,
    embroideryOptions,
  } = EmbroideryService.useContainer()
  const { hasMandatoryLogoEmbroidery, hasPortalLogoEmbroideryOptions, portal } = usePortal()

  // Switch out embroidery section with sneak peek if the user has explicitly confirmed their embroidery selection from the modal
  const hasSelectedOptions =
    confirmAddEmbroidery &&
    selectedEmbroideryFormOptions &&
    !!Object.keys(selectedEmbroideryFormOptions).length

  // Only display the portal embroidery section if:
  // 1. A portal color is currently selected
  // 2. The current product category enforces mandatory logo embroidery
  // 3. The current embroidery options contain the expected portal-specific `LOGO_ONLY`/`LOGO_TEXT` options
  const displayPortalEmbroiderySection =
    isPortalColorSelected &&
    hasMandatoryLogoEmbroidery({ categoryAnalyticsId }) &&
    hasPortalLogoEmbroideryOptions(embroideryOptions) &&
    selectedEmbroideryOptions.LOGO_ONLY

  const {
    embroidery: { discountText },
  } = useTranslation()

  // The discount message to display within the summary section
  const summaryDiscountMessage = useMemo(
    () =>
      isPortalColorSelected && portal?.embroidery?.embroideryDiscountPercentage
        ? discountText.summarySection.replace(
            '{{discountPercentage}}',
            portal.embroidery.embroideryDiscountPercentage.toString()
          )
        : undefined,
    [discountText.summarySection, portal, isPortalColorSelected]
  )

  return (
    <>
      {embroiderable && !hasSelectedOptions && (
        <>
          <EmbroideryCtaSection
            isPortalColorSelected={isPortalColorSelected}
            isPortalSelectionInvalid={addToBagErrors?.missingRequiredTextEmbroidery}
          />
          {displayPortalEmbroiderySection && <PortalEmbroiderySection />}
        </>
      )}

      {hasSelectedOptions && (
        <>
          <EmbroiderySummary
            resetInvalidBagClickStatus={resetInvalidBagClickStatus}
            discountMessage={summaryDiscountMessage}
            withPricing
          />
          <EmbroiderySectionWrapper className='embroidery-settings-section-disclosure'>
            <EmbroideryDisclosure />
          </EmbroiderySectionWrapper>
        </>
      )}
    </>
  )
}
